/**
 * Shared utilities for working with enriched data and WarpDrive
 * reactive record field access.
 *
 * Centralizes the `as unknown as Record` workaround needed because
 * WarpDrive `WithLegacy` records throw on unexpected property access.
 */

/**
 * Safely access a field on a reactive WarpDrive record by key.
 * Wraps the property access in a try/catch because WarpDrive records
 * can throw on unknown properties.
 */
export function field(obj: unknown, key: string): unknown {
  const rec = obj as Record<string, unknown> | null | undefined;
  if (!rec) return undefined;
  try {
    return rec[key];
  } catch {
    return undefined;
  }
}

/**
 * Coerce an unknown value to a string.
 * Numbers are stringified, everything else returns ''.
 */
export function asString(value: unknown): string {
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return String(value);
  return '';
}

/**
 * Coerce an unknown value to a date-compatible type for `formatDate`.
 * Returns undefined for booleans and other non-date-like values.
 */
export function asDateValue(
  value: unknown
): string | number | Date | undefined {
  if (value == null || typeof value === 'boolean') return undefined;
  if (
    typeof value === 'string' ||
    typeof value === 'number' ||
    value instanceof Date
  ) {
    return value;
  }
  return undefined;
}

/**
 * Parse an enriched data blob (possibly JSON string) into a record.
 */
export function parseEnrichedData(
  raw: unknown
): Record<string, unknown> | null {
  if (!raw) return null;
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw) as Record<string, unknown>;
    } catch {
      return null;
    }
  }
  return raw as unknown as Record<string, unknown>;
}

/**
 * Cast a reactive record into a plain Record for field access.
 * Handles null/undefined by returning an empty object.
 */
export function recordAsMap(obj: unknown): Record<string, unknown> {
  if (!obj) return {};
  return obj as unknown as Record<string, unknown>;
}
